import React, { useState } from 'react';
import 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useUpdateUser from '@teetsh/app/src/queries/user/useUpdateUser';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import useEDT from '@teetsh/app/src/queries/edt/useEDT';
import { useLocation, useParams } from 'react-router-dom';
import qs from 'qs';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@teetsh/app/src/components/Resizable';
import { Sidebar } from '@teetsh/app/src/containers/EmploiDuTemps/Preview/Sidebar';
import { Body } from '@teetsh/app/src/containers/EmploiDuTemps/Preview/Body';
import { UserProfile } from '@teetsh/app/src/types/user';
import { Form } from 'react-final-form';
import AutoSubmitForm from '@teetsh/app/src/components/AutoSave';
import { useUserAccount } from 'queries/user/account/useUserAccount';
import { getMutualWorkingDays } from '@teetsh/app/src/containers/utils';
import { EmploiDuTemps } from '@teetsh/app/src/types/edt';

interface EDTPreviewContentProps {
  edt: EmploiDuTemps;
}

function EDTPreviewContent({ edt }: EDTPreviewContentProps) {
  const user = useUser();
  const updateUser = useUpdateUser();
  const { data: userAccount } = useUserAccount();

  const params = useParams<{
    schoolyearId: string;
    schoolId: string;
    emploiDuTempsId: string;
  }>();
  const edtId = params.emploiDuTempsId as string;

  const classrooms = useClassrooms();
  const location = useLocation();
  const { classrooms: requestedClassrooms } = qs.parse(
    location.search.substr(1),
  );
  const [classroomsToDisplay, setClassroomsToDisplay] = useState<string[]>(
    // if we detect multiple classes but no one has been specified, we display all of them
    classrooms.length > 1 && !requestedClassrooms
      ? classrooms.map(({ id }) => id)
      : requestedClassrooms?.split(',') || [],
  );

  const [edtTitle, setEdtTitle] = useState<string>(edt.name);

  const displayedClassrooms = classrooms.filter(cl =>
    classroomsToDisplay.includes(cl.id),
  );

  const availableWorkingDays =
    displayedClassrooms.length > 0
      ? getMutualWorkingDays(displayedClassrooms)
      : getMutualWorkingDays(classrooms);

  const [daysToDisplay, setDaysToDisplay] = useState<number[]>(
    () => availableWorkingDays,
  );

  const validDaysToDisplay = daysToDisplay.filter(day =>
    availableWorkingDays.includes(day),
  );

  return (
    <Form<UserProfile>
      initialValues={user}
      onSubmit={async values => {
        await updateUser(values);
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="w-full h-full flex flex-col overflow-hidden">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="edt-preview-sidebar"
            >
              <Sidebar
                edtId={edtId}
                edtName={edt.name}
                edtTitle={edtTitle}
                setEdtTitle={setEdtTitle}
                classrooms={classrooms}
                classroomsToDisplay={classroomsToDisplay}
                setClassroomsToDisplay={setClassroomsToDisplay}
                daysToDisplay={daysToDisplay}
                setDaysToDisplay={setDaysToDisplay}
                limitedBgSet={userAccount?.isOnOldPricing || false}
              />
              <ResizableHandle />
              {/* TODO: clean defaultSize later if useless */}
              <ResizablePanel order={1} defaultSize={76}>
                <div tw="h-full overflow-auto">
                  <Body
                    schoolyearId={params.schoolyearId as string}
                    schoolId={params.schoolId as string}
                    edtId={edtId}
                    lng={user.locale}
                    classroomsWithIdsJoined={classroomsToDisplay.join(',')}
                    daysToDisplay={validDaysToDisplay}
                    edtTitle={edtTitle}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}

export function EDTPreview() {
  const params = useParams<{
    schoolyearId: string;
    schoolId: string;
    emploiDuTempsId: string;
  }>();
  const edtId = params.emploiDuTempsId as string;
  const { data: edt } = useEDT({ edtId, enabled: true });

  if (!edt) {
    return null;
  }

  return <EDTPreviewContent edt={edt} />;
}
